import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';

import { Box } from '../css';
import RegistrationModal from '../modal';
import {
  RECAPTCHA_SITE_KEY,
  CONSTANT,
  partnerNames
} from '../../utils/constant';
import { Field, Channels, Outlet } from './fields';
import PasswordMessage from '../passwordMessage';
import Dealership from './fields/dealership';
import { getLocale } from '../../utils';

const salesManagerForm = ({
  labels,
  schema,
  onSubmit,
  initialValues,
  loading,
  onCaptchaChange,
  channels,
  dealerships,
  provinces,
  showModal,
  confirmationDialog,
  isRedirectedFromPassport,
  userRole
}) => {
  const isChannelCorpStore = channel => {
    if (!channel || !channel.length) return false;
    return [CONSTANT.CHANNELS.CORP_STORE].includes(channel[0].id);
  };
  const isChannelDealer = channel => {
    if (!channel || !channel.length) return false;
    return [CONSTANT.CHANNELS.DEALER].includes(channel[0].id);
  };
  const isChannelTqPartenaire = channel => {
    if (!channel || !channel.length) return false;
    return [CONSTANT.CHANNELS.TQ_PARTENAIRE].includes(channel[0].id);
  };
  const generalChannels = [
    CONSTANT.CHANNELS.BCX_CARE,
    CONSTANT.CHANNELS.HEALTH,
    CONSTANT.CHANNELS.CSD,
    CONSTANT.CHANNELS.NBD,
    CONSTANT.CHANNELS.ADT
  ];
  const isOutletAndCpmsRequired = channel => {
    if (!channel || !channel.length) return false;
    return !generalChannels.includes(channel[0].id);
  };
  const isXidRequired = channel => {
    if (!channel || !channel.length) return false;
    return generalChannels.includes(channel[0].id);
  };
  const updatedProvinces = [
    { value: '', text: '' },
    ...provinces.map(i => ({ ...i, value: i.id, text: i.name }))
  ];
  const getSalesManagerLabel = label =>
    userRole === 2 && getLocale() === 'EN' ? `Sales ${label}` : label;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values, actions) => {
        onSubmit(values, actions);
      }}
    >
      {({ values, errors, touched, setFieldValue, setFieldTouched }) => {
        return (
          <Form>
            {showModal && <RegistrationModal show />}
            <Field
              name="firstName"
              label={labels.FIRST_NAME}
              disabled={isRedirectedFromPassport}
              touched={touched}
              errors={errors}
              required
            />
            <Field
              name="lastName"
              label={labels.LAST_NAME}
              disabled={isRedirectedFromPassport}
              touched={touched}
              errors={errors}
              required
            />
            <Field
              type="email"
              name="email"
              label={labels.EMAIL}
              disabled={isRedirectedFromPassport}
              touched={touched}
              errors={errors}
              comment={`${labels.NOTE}: ${labels.EMAIL_MESSAGE}`}
              required
            />
            <Field
              type="password"
              name="password"
              label={labels.PASSWORD}
              touched={touched}
              errors={errors}
              style={{ width: '100%' }}
              required
            />
            <PasswordMessage labels={labels} />
            <Field
              name="province"
              label={labels.SELECT_PROVINCE}
              touched={touched}
              errors={errors}
              required
              style={{ width: '100%' }}
              list={updatedProvinces}
            />
            <Channels
              label={labels.SELECT_CHANNEL}
              touched={touched}
              errors={errors}
              channels={channels}
              values={values}
              setFieldValue={setFieldValue}
            />
            {isChannelDealer(values.channel) && (
              <>
                <Dealership
                  label={labels.SELECT_DEALERSHIP}
                  errors={errors}
                  touched={touched}
                  dealerships={dealerships}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />
                <Field
                  name="channelOrgCode"
                  label={labels.CHANNEL_ORGANIZATION_CODE}
                  errors={errors}
                  touched={touched}
                  required
                />
              </>
            )}
            <Field
              name="contactNumber"
              label={labels.CONTACT_NUMBER}
              touched={touched}
              errors={errors}
              required
            />
            {isOutletAndCpmsRequired(values.channel) && (
              <>
                <Field
                  name="salesRepId"
                  label={labels.SALES_REP_ID}
                  touched={touched}
                  errors={errors}
                  required
                />
                <Outlet
                  labels={labels}
                  touched={touched}
                  errors={errors}
                  channels={channels}
                  setFieldValue={setFieldValue}
                />
              </>
            )}
            {isXidRequired(values.channel) && (
              <Field
                name="xid"
                label={labels.XID_TID}
                touched={touched}
                errors={errors}
                required
              />
            )}
            {isChannelCorpStore(values.channel) && (
              <Field
                name="outletCode"
                label={labels.OUTLET_CODE}
                touched={touched}
                errors={errors}
                required
              />
            )}
            {isChannelTqPartenaire(values.channel) && (
              <Field
                name="partnerId"
                label={labels.PARTNER}
                errors={errors}
                touched={touched}
                required
                style={{ width: '100%' }}
                list={[{ value: '', text: '' }, ...partnerNames]}
              />
            )}
            <Field
              name="salesManager"
              label={getSalesManagerLabel(labels.SALE_MANAGER)}
              touched={touched}
              errors={errors}
              required={!isChannelDealer}
            />
            <Field
              type="email"
              name="salesManagerEmail"
              label={getSalesManagerLabel(labels.SALE_MANAGER_EMAIL)}
              touched={touched}
              errors={errors}
              required={!isChannelDealer}
            />
            <Box>
              <div
                style={{ position: 'relative' }}
                className={`input-container ${
                  errors.recaptcha && touched.recaptcha ? 'error' : ''
                }`}
              >
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  name="recaptcha"
                  onChange={onCaptchaChange}
                />
              </div>
            </Box>
            <div id="continue_button" style={{ marginTop: 20 }}>
              {!loading ? (
                <span>
                  <button
                    type="submit"
                    className="frg-button button-wide color-green"
                  >
                    {labels.CONTINUE}
                  </button>
                </span>
              ) : (
                <span>
                  <button
                    className="frg-button button-wide color-green"
                    type="button"
                  >
                    <i className="fa fa-refresh fa-spin" />
                    {confirmationDialog.LOADING}
                  </button>
                </span>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

salesManagerForm.propTypes = {
  labels: PropTypes.objectOf(PropTypes.any).isRequired,
  schema: PropTypes.objectOf(PropTypes.any).isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.objectOf(PropTypes.any).isRequired,
  loading: PropTypes.bool.isRequired,
  onCaptchaChange: PropTypes.func.isRequired,
  channels: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string
    })
  ).isRequired,
  provinces: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })
  ).isRequired,
  dealerships: PropTypes.arrayOf(PropTypes.any).isRequired,
  showModal: PropTypes.bool.isRequired,
  confirmationDialog: PropTypes.objectOf(PropTypes.any).isRequired,
  isRedirectedFromPassport: PropTypes.bool.isRequired,
  userRole: PropTypes.number.isRequired
};

export default salesManagerForm;
